import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatarDinheiro } from '../../utils';

interface Props {
  eventoId: number;
}

interface Total {
  pessoas: number;
  pago: number;
  falta: number;
  suplentes: number;
}

const VAZIO: Total = { pessoas: 0, pago: 0, falta: 0, suplentes: 0 };

// Acampamento: separa o dinheiro do evento em Equipe e Campistas (por modalidade).
export default function TotaisPublicos({ eventoId }: Props) {
  const [totais, setTotais] = useState<{ equipe: Total; campista: Total } | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [resMods, resSaldos] = await Promise.all([
          supabase.from('modalidades').select('id, papel').eq('evento_id', eventoId),
          supabase
            .from('vw_saldo_inscricao')
            .select('inscricao_id, modalidade_id, status, pago_centavos, saldo_centavos')
            .eq('evento_id', eventoId),
        ]);
        if (resMods.error) throw resMods.error;
        if (resSaldos.error) throw resSaldos.error;
        if (!ativo) return;

        const papelPorModalidade = new Map<number, string>();
        ((resMods.data as { id: number; papel: string | null }[]) || []).forEach((m) => {
          if (m.papel) papelPorModalidade.set(Number(m.id), m.papel);
        });

        const equipe: Total = { ...VAZIO };
        const campista: Total = { ...VAZIO };
        ((resSaldos.data as {
          modalidade_id: number;
          status: string;
          pago_centavos: number | null;
          saldo_centavos: number | null;
        }[]) || []).forEach((l) => {
          if (l.status === 'cancelada') return;
          const papel = papelPorModalidade.get(Number(l.modalidade_id));
          const alvo = papel === 'equipe' ? equipe : papel === 'campista' ? campista : null;
          if (!alvo) return;
          alvo.pago += Number(l.pago_centavos) || 0;
          // Suplente ainda não deve nada: só paga se ganhar a vaga.
          if (l.status === 'lista_espera') {
            alvo.suplentes += 1;
            return;
          }
          alvo.pessoas += 1;
          alvo.falta += Math.max(0, Number(l.saldo_centavos) || 0);
        });
        setTotais({ equipe, campista });
      } catch {
        if (ativo) setErro(true);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  if (erro) return null;

  const linhas = [
    { rotulo: 'Equipe', total: totais?.equipe ?? VAZIO, cor: 'bg-primary-500' },
    { rotulo: 'Campistas', total: totais?.campista ?? VAZIO, cor: 'bg-accent-500' },
  ];

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <h2 className="text-[15px] font-bold text-foreground-950">Equipe e Campistas</h2>
      <p className="mt-0.5 text-[13px] text-foreground-600">
        O mesmo dinheiro do evento, separado por público.
      </p>
      <div className="mt-3 space-y-3">
        {linhas.map((l) => (
          <div key={l.rotulo} className="flex items-center gap-3">
            <span className={`h-8 w-1.5 shrink-0 rounded-full ${l.cor}`} />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold text-foreground-900">
                {l.rotulo}{' '}
                <span className="font-normal text-foreground-500">
                  · {l.total.pessoas} com vaga
                </span>
              </p>
              <p className="mt-0.5 text-[13px] text-foreground-600">
                Recebido <strong className="font-semibold text-foreground-900">{formatarDinheiro(l.total.pago)}</strong>
                {l.total.falta > 0
                  ? ` · falta ${formatarDinheiro(l.total.falta)} (com vaga)`
                  : ' · tudo pago'}
              </p>
              {l.total.suplentes > 0 && (
                <p className="mt-0.5 text-[12px] text-foreground-400">
                  {l.total.suplentes}{' '}
                  {l.total.suplentes === 1
                    ? 'suplente não entra na conta'
                    : 'suplentes não entram na conta'}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}